import {workingHourForTheDay, getHolidaysOfTheDay} from '@/forecast-app/my-work-tab/my-timesheets-page/timesheets_person_data';
import {getLockedDate} from '@/components/timesheets/time-lock/TimeLockUtil';
import {APPROVAL_STATUS} from '@/constants';

/**
 * Last day of the visible week where the person is actively working
 * (working-hours > 0 AND no holiday on the holiday calendar). Iterates the
 * week from end backward, returning the first matching day.
 *
 * Returns `weekViewDate` (a fallback Moment) if no working day is found in
 * the week (e.g. all-holiday week, or person inactive for the entire week).
 *
 * Centralised here so both My Timesheets and the Time Approval page synthesis
 * use the same primitive, preventing drift between the two consumers.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lastWorkingDayInWeek = (person: any, weekViewDate: any): any => {
	const weekStart = weekViewDate.clone().startOf('week');
	const weekArray = Array.from({length: 7}, (_, i) => weekStart.clone().add(i, 'day'));
	const found = weekArray
		.slice()
		.reverse()
		.find(day => {
			const workingHours = workingHourForTheDay(person, day);
			const holidaysForDay = getHolidaysOfTheDay(person, day);
			return workingHours !== 0 && holidaysForDay.length === 0;
		});
	return found ?? weekViewDate;
};

/**
 * Single source of truth for a person's week-level approval status.
 *
 * Precedence (highest first):
 *   1. If `timeRegs` contains any project rows (non-idle), derive status from
 *      row.approvalStatus values. Under the new statuses that row hierarchy is
 *      action-first — DECLINED > PENDING > SUBMITTED > APPROVED > INVOICED — so a
 *      week reads as the next thing its owner has to do to it.
 *   2. NEW (PIKA-519): If `personWeekApprovals` contains a row for this week
 *      with status APPROVED or DECLINED, return that (sticky approver decision).
 *      Status SUBMITTED in the PWA row is neutral — falls through to step 3.
 *   3. If `timeRegs` is empty (after filtering idle) AND the person's effective
 *      submission lock is at or after `lastWorkingDayInWeek(person, weekViewDate)`,
 *      return SUBMITTED.
 *   4. Otherwise return NOT_SUBMITTED (FF OFF) or PENDING (FF ON).
 *
 * Why this precedence: time-reg rows are the more specific signal. PWA rows
 * APPROVED/DECLINED are the approver's explicit decision (sticky). The lock-check
 * is the lowest-precision signal — only fires when nothing more specific applies.
 *
 * Consumers:
 *   - TimesheetsBottomSection.getTimesheetsApprovalStatus (My Timesheets)
 *   - TimeApprovalSection synthesis filter (Time Approval page)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getApprovalStatusForWeek = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	person: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	company: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	weekViewDate: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[] | null | undefined,
	hasNewStatuses: boolean,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	personWeekApprovals: any[] | null | undefined = []
): string => {
	const rows = timeRegs ?? [];
	const projectRows = rows.filter(t => !t.idleTime);

	// Step 2: PWA row sticky-state check (PIKA-519). Only APPROVED/DECLINED count;
	// SUBMITTED is neutral (defer to lock-based step 3).
	if (projectRows.length === 0) {
		const weekStart = weekViewDate.clone().startOf('week');
		const pwaRow = (personWeekApprovals ?? []).find(
			(r: {weekStartYear: number; weekStartMonth: number; weekStartDay: number}) =>
				r.weekStartYear === weekStart.year() &&
				r.weekStartMonth === weekStart.month() + 1 &&
				r.weekStartDay === weekStart.date()
		);
		if (
			pwaRow &&
			(pwaRow.approvalStatus === APPROVAL_STATUS.APPROVED || pwaRow.approvalStatus === APPROVAL_STATUS.DECLINED)
		) {
			return pwaRow.approvalStatus;
		}
	}

	// Step 3 + 4: empty rows → consult lock
	if (projectRows.length === 0) {
		const lockedDate = getLockedDate(company, person);
		const lastWorkingDay = lastWorkingDayInWeek(person, weekViewDate);
		if (lockedDate && lastWorkingDay && lockedDate.isSameOrAfter(lastWorkingDay, 'day')) {
			return APPROVAL_STATUS.SUBMITTED;
		}
		return hasNewStatuses ? APPROVAL_STATUS.PENDING : APPROVAL_STATUS.NOT_SUBMITTED;
	}

	// Step 1 (FF OFF): mirror existing TimesheetsBottomSection logic — weakest-link semantics.
	// Any NOT_SUBMITTED row drags the week to NOT_SUBMITTED, even if other rows are SUBMITTED.
	if (!hasNewStatuses) {
		let status: string = APPROVAL_STATUS.APPROVED;
		for (const row of projectRows) {
			if (!row || row.approvalStatus === APPROVAL_STATUS.NOT_SUBMITTED) {
				return APPROVAL_STATUS.NOT_SUBMITTED;
			} else if (row.approvalStatus === APPROVAL_STATUS.SUBMITTED) {
				status = APPROVAL_STATUS.SUBMITTED;
			}
		}
		return status;
	}

	// FF on: full new status hierarchy, ordered by what the week still asks of its owner rather
	// than by how far it has progressed. A week reads as whatever needs doing to it next.
	//
	// PENDING therefore outranks SUBMITTED: a week reported SUBMITTED the moment a single row
	// was, so a part-submitted week claimed to be finished while the rest of it had never
	// reached an approver — and the single action button, which reads this status, offered only
	// Unsubmit. Ranking the outstanding time first makes the badge and the button agree on the
	// thing the owner still has to do.
	//
	// DECLINED stays above it: the approver has asked for a correction, and that is the more
	// urgent ask. APPROVED and INVOICED stay at the bottom — they are terminal, and any row
	// short of them is what the week is still waiting on.
	if (projectRows.some(t => t.approvalStatus === APPROVAL_STATUS.DECLINED)) return APPROVAL_STATUS.DECLINED;
	if (projectRows.some(t => t.approvalStatus === APPROVAL_STATUS.PENDING)) return APPROVAL_STATUS.PENDING;
	if (projectRows.some(t => t.approvalStatus === APPROVAL_STATUS.NOT_SUBMITTED)) return APPROVAL_STATUS.PENDING;
	if (projectRows.some(t => t.approvalStatus === APPROVAL_STATUS.SUBMITTED)) return APPROVAL_STATUS.SUBMITTED;
	if (projectRows.some(t => t.approvalStatus === APPROVAL_STATUS.APPROVED)) return APPROVAL_STATUS.APPROVED;
	if (projectRows.some(t => t.approvalStatus === APPROVAL_STATUS.INVOICED)) return APPROVAL_STATUS.INVOICED;
	return APPROVAL_STATUS.NOT_SUBMITTED;
};

/**
 * Group owned-projects-persons by their week-level computed status.
 *
 * Used by the Time Approval page synthesis to route each owned person to the
 * matching bucket: NOT_SUBMITTED → Not Submitted tab, PENDING → Pending tab,
 * SUBMITTED → Submitted tab (the 0h-locked case), etc. Persons with time-regs
 * land in whatever bucket their row statuses imply (via getApprovalStatusForWeek);
 * persons without time-regs land in SUBMITTED if their effective lock covers the
 * week, otherwise in NOT_SUBMITTED (FF OFF) / PENDING (FF ON).
 *
 * Returns a Map keyed by APPROVAL_STATUS value. Empty inputs return an empty Map
 * (callers use `Map.get(status) ?? []` to handle missing buckets).
 *
 * Time-regs without a person.id (orphan rows) are silently skipped — they can't
 * be attributed to a person, so they don't influence anyone's status.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const synthesizeOwnedPersonsByStatus = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ownedPersonsEdges: any[] | null | undefined,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	timeRegs: any[] | null | undefined,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	company: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	weekViewDate: any,
	hasNewStatuses: boolean
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): Map<string, any[]> => {
	const timeRegsByPersonId = new Map();
	(timeRegs ?? []).forEach(tReg => {
		const pid = tReg.person?.id;
		if (!pid) return;
		const arr = timeRegsByPersonId.get(pid) ?? [];
		arr.push(tReg);
		timeRegsByPersonId.set(pid, arr);
	});

	const byStatus = new Map();
	(ownedPersonsEdges ?? []).forEach(p => {
		const personRows = timeRegsByPersonId.get(p.node.id) ?? [];
		// PIKA-519: pass the person's weekApprovals through so step 2 of the precedence
		// can pick up APPROVED/DECLINED 0h-week states. Persons fetched without this
		// field (older callers) get [] and behave per pre-PIKA-519 precedence.
		const pwaRows = p.node.weekApprovals ?? [];
		const status = getApprovalStatusForWeek(p.node, company, weekViewDate, personRows, hasNewStatuses, pwaRows);
		if (!byStatus.has(status)) byStatus.set(status, []);
		byStatus.get(status).push(p);
	});
	return byStatus;
};
